import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse, AxiosError } from "axios";
import { CapabilityDTO } from "../types/capabilityDTO";
import Problem from "../types/problem";
import apiClient from "../main/apiClient";

const useFetchCapabilities = (organisationId: string) => {
  return useQuery<CapabilityDTO[], AxiosError>({
      queryKey: ["capability"],
      queryFn: () =>
        apiClient.get(`/organisation/${organisationId}/capability`).then((resp) => resp.data),
  });
}

const useFetchCapability = (organisationId: string, capabilityId: string) => {
  return useQuery<CapabilityDTO, AxiosError>({
    queryKey: ["capability", capabilityId],
    queryFn: () =>
      apiClient.get(`/organisation/${organisationId}/capability/${capabilityId}`).then((resp) => resp.data),
  });
};

const useUpdateCapability = (organisationId: string) => {
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse, AxiosError<Problem>, CapabilityDTO>({
    mutationFn: (capabilityDTO) => apiClient.put(`/organisation/${organisationId}/capability`, capabilityDTO),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["capability"] });
    }
  });
};

const useAddCapability = (organisationId: string, setCapabilityState: (capability: CapabilityDTO) => void) => {
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse, AxiosError<Problem>, CapabilityDTO>({
    mutationFn: (capabilityDTO) => apiClient.post(`/organisation/${organisationId}/capability`, capabilityDTO),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["capability"] });
    },
    onError: (error, variables) => {
      console.log(error);
      setCapabilityState(variables); // Preserve the state on error
    }
  });
};

const useDeleteCapability = (organisationId: string) => {
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse, AxiosError<Problem>, CapabilityDTO>({
    mutationFn: (capabilityDTO) => apiClient.delete(`/organisation/${organisationId}/capability`, { data: capabilityDTO }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["capability"] });
      queryClient.invalidateQueries({ queryKey: ["user"] });
      queryClient.invalidateQueries({ queryKey: ["capabilitiesForUnits"] });
      queryClient.invalidateQueries({ queryKey: ["capabilitiesForUnitMission"] });
    }
  });
};

export { useFetchCapabilities, useFetchCapability, useUpdateCapability, useAddCapability, useDeleteCapability }
